import React, { useState, useEffect, useContext, useRef } from 'react';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import Table from 'examples/Tables/Table';
import Card from '@mui/material/Card';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { Edit } from '@mui/icons-material';
import {
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Chip,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';

import { testSalesforceConnection, refreshSalesforceToken } from 'services/salesforce';
import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext';

function OrgTable({ orgs, onEdit, onDelete }) {
  const [columns] = useState([
    { name: 'name', align: 'left' },
    { name: 'endpoint', align: 'left' },
    { name: 'org_type', align: 'center' },
    { name: 'status', align: 'center' },
    { name: 'action', align: 'center' },
  ]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [dialogText, setDialogText] = useState('');

  // --- CHANGE 1: New state to track status of each org individually ---
  // It will store data like: { "org_id_1": { isConnected: true, error: null }, "org_id_2": { isConnected: false, error: ... } }
  const [orgStatuses, setOrgStatuses] = useState({});

  const { userId } = useContext(UserContext);
  const { addNotification } = useNotifications();
  const recentlyRefreshedOrgIds = useRef(new Set());

  // --- CHANGE 2: This useEffect now runs in the background to check statuses ---
  // It no longer controls a global loading state.
  useEffect(() => {
    // If there are no orgs or no user, do nothing.
    if (!userId || !orgs || orgs.length === 0) {
      setOrgStatuses({}); // Clear statuses if orgs list is empty
      return;
    }

    // Reset the refresh tracker when the org list changes
    recentlyRefreshedOrgIds.current.clear();

    // Function to check a single org's status
    const checkOrgStatus = async (org) => {
      const orgId = org.org_id;
      let isConnected = false;
      let connectionError = null;

      if (org.salesforce_access_token && org.instance_url) {
        try {
          await testSalesforceConnection(orgId);
          isConnected = true;
        } catch (testError) {
          if (testError.details === 'INVALID_SESSION_ID' && !recentlyRefreshedOrgIds.current.has(orgId)) {
            recentlyRefreshedOrgIds.current.add(orgId);
            addNotification({
              title: ['Session Invalid', org.name],
              details: 'Attempting to refresh Salesforce token...',
              severity: 'info',
            });
            try {
              await refreshSalesforceToken(orgId);
              isConnected = true;
              addNotification({
                title: ['Token Refreshed', org.name],
                details: 'Successfully refreshed the access token.',
                severity: 'success',
              });
            } catch (refreshError) {
              isConnected = false;
              connectionError = refreshError;
              addNotification({
                title: ['Token Refresh Failed', org.name],
                details: `Could not refresh token: ${refreshError.message}`,
                severity: 'error',
              });
            }
          } else {
            isConnected = false;
            connectionError = testError;
          }
        }
      } else {
        isConnected = false;
        connectionError = { message: 'Missing Access Token or Instance URL.', details: 'MISSING_CREDENTIALS' };
      }

      // Update the state for this specific org
      setOrgStatuses((prevStatuses) => ({
        ...prevStatuses,
        [orgId]: { isConnected, error: connectionError },
      }));
    };

    // Kick off the status check for all orgs. They will run in parallel.
    orgs.forEach(checkOrgStatus);
  }, [orgs, userId, addNotification]); // Rerun when the org list changes

  // --- CHANGE 3: Build the rows on every render ---
  // The table is now built immediately. The status column will update reactively.
  const rows = (orgs || []).map((org) => {
    const statusInfo = orgStatuses[org.org_id]; // Get the status for the current org

    return {
      name: (
        <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
          {org.name || 'Unnamed Org'}
        </ArgonTypography>
      ),
      endpoint: (
        <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
          {org.instance_url || 'N/A'}
        </ArgonTypography>
      ),
      org_type: (
        <Tooltip title={org.org_type || 'Type N/A'} placement="top">
          {org.org_type ? (
            (() => {
              switch (org.org_type) {
                case 'Production':
                  return <Chip icon={<CircleIcon />} label="Prod" color="error" variant="outlined" size="small" />;
                case 'Developer':
                  return <Chip icon={<CircleIcon />} label="Dev" color="success" variant="outlined" size="small" />;
                case 'Testing':
                  return <Chip icon={<CircleIcon />} label="Test" color="primary" variant="outlined" size="small" />;
                default:
                  return <Chip label={org.org_type} color="default" variant="outlined" size="small" />;
              }
            })()
          ) : (
            <Chip label="Unknown" variant="outlined" size="small" />
          )}
        </Tooltip>
      ),
      // --- CHANGE 4: Conditionally render the status cell ---
      status: (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 24 }}>
          {!statusInfo ? (
            // If statusInfo is not yet available, show a spinner
            <Tooltip title="Connecting" placement="top">
              <CircularProgress size={14} />
            </Tooltip>
          ) : statusInfo.isConnected ? (
            // If connected, show the check mark
            <Tooltip title="Connected" placement="top">
              <CheckCircleIcon color="success" />
            </Tooltip>
          ) : (
            // If not connected, show the cancel icon with the error
            <Tooltip
              title={`Disconnected: ${statusInfo.error?.message || 'Unknown Error'} (${
                statusInfo.error?.details || 'N/A'
              })`}
              placement="top"
            >
              <CancelIcon color="error" />
            </Tooltip>
          )}
        </Box>
      ),
      action: (
        <ArgonBox display="flex" justifyContent="space-evenly" alignItems="center" sx={{ gap: 2 }}>
          <Tooltip title="Edit" placement="top">
            <IconButton
              aria-label="edit"
              onClick={(e) => {
                e.preventDefault();
                onEdit(org);
              }}
              color="secondary"
              size="small"
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Org" placement="top">
            <IconButton aria-label="delete" onClick={() => handleConfirmDelete(org)} color="secondary" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </ArgonBox>
      ),
    };
  });

  const handleConfirmDelete = (org) => {
    setOrgToDelete(org.org_id);
    setDialogText(org.name);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setOrgToDelete(null);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(false);
    if (orgToDelete) {
      onDelete(orgToDelete);
    }
    setOrgToDelete(null);
  };

  return (
    <ArgonBox mb={3}>
      <Card>
        <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          <ArgonTypography variant="h6">Salesforce Orgs</ArgonTypography>
        </ArgonBox>
        <ArgonBox
          sx={{
            '& .MuiTableRow-root:not(:last-child)': {
              '& td': {
                borderBottom: ({ borders: { borderWidth, borderColor } }) => `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
        >
          {/* --- CHANGE 5: Simplified render logic --- */}
          {/* We no longer need a global loading spinner, just check if there are rows to display */}
          {rows.length > 0 ? (
            <Table columns={columns} rows={rows} />
          ) : (
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="body2" color="textSecondary">
                No Salesforce organizations configured or found.
              </ArgonTypography>
            </ArgonBox>
          )}
        </ArgonBox>
      </Card>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirm Delete'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ textAlign: 'center' }}>
            Are you sure you want to delete 
            <ArgonTypography component="span" variant="body2" textTransform="none" color="primary">
              {dialogText}
            </ArgonTypography>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </ArgonBox>
  );
}

export default OrgTable;
